import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SideNavbar from '../../components/Navbar/SideNavbar';
import Header from '../../components/Header/Header';
import { Table, Button, Tag, Space, Input, Select, Modal, Card, Collapse } from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import {
    getShippingRulesService,
    exportShippingRulesService,
    getShippingRuleDetailService,
    getCategoryLevelOneService
} from '../../../core/services/shippingService';
import ShippingBulkUploadModal from '../../components/ShippingPortal/ShippingBulkUploadModal';

const ShippingPortalListing: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});

    // Bulk upload modal state
    const [bulkModalVisible, setBulkModalVisible] = useState(false);

    // Export modal state
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportEmail, setExportEmail] = useState('');
    const [exportCategoryIds, setExportCategoryIds] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    // Rule Detail modal state
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedRuleDetail, setSelectedRuleDetail] = useState<any>(null);

    const fetchCategories = async () => {
        try {
            const res = await getCategoryLevelOneService();
            if (res?.data?.data) {
                const map: Record<string, string> = {};
                if (Array.isArray(res.data.data)) {
                    res.data.data.forEach((cat: any) => {
                        const cid = String(cat.category_id || cat.categoryId || cat.id);
                        const cname = String(cat.category_name || cat.categoryName || cat.name || cid);
                        map[cid] = cname;
                    });
                } else if (typeof res.data.data === 'object') {
                    Object.entries(res.data.data).forEach(([catId, catName]) => {
                        map[String(catId)] = String(catName);
                    });
                }
                setCategoryMap(map);
            }
        } catch (err) {
            console.error('Failed to fetch category mapping', err);
        }
    };

    const fetchRules = async () => {
        setLoading(true);
        try {
            const [rulesRes] = await Promise.all([
                getShippingRulesService(),
                fetchCategories()
            ]);
            if (rulesRes?.data?.data) {
                const list = Array.isArray(rulesRes.data.data) ? rulesRes.data.data : [rulesRes.data.data];
                setData(list);
                setFilteredData(list);
            } else if (rulesRes?.data && Array.isArray(rulesRes.data)) {
                setData(rulesRes.data);
                setFilteredData(rulesRes.data);
            } else {
                setData([]);
                setFilteredData([]);
            }
        } catch (err: any) {
            console.error('Failed to fetch shipping rules', err);
            toast.error('Failed to load shipping rules');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRules();
    }, []);

    useEffect(() => {
        let result = [...data];
        if (searchText) {
            const lower = searchText.toLowerCase();
            result = result.filter(item => {
                const idStr = item.shippingCriteriaId ? String(item.shippingCriteriaId).toLowerCase() : '';
                const priorityStr = item.priority ? String(item.priority).toLowerCase() : '';
                const catName = (item.shippingCriteriaId && categoryMap[String(item.shippingCriteriaId)])
                    ? categoryMap[String(item.shippingCriteriaId)].toLowerCase()
                    : '';
                return idStr.includes(lower) || priorityStr.includes(lower) || catName.includes(lower);
            });
        }
        if (statusFilter !== 'ALL') {
            const isActive = statusFilter === 'ACTIVE';
            result = result.filter(item => {
                const rawActive = item.active ?? item.shippingAttr?.active;
                const activeValue = rawActive === true || rawActive === 'true' || rawActive === '1' || rawActive === 1;
                return activeValue === isActive;
            });
        }
        setFilteredData(result);
    }, [searchText, statusFilter, data, categoryMap]);

    const handleExport = async () => {
        if (!exportEmail.trim()) {
            toast.error('Please enter email address for export report.');
            return;
        }
        setExportLoading(true);
        try {
            const res = await exportShippingRulesService(exportCategoryIds, exportEmail);
            if (res?.data?.status || res?.status === 200) {
                toast.success('Export request initiated successfully. Report will be emailed.');
                setExportModalVisible(false);
                setExportEmail('');
                setExportCategoryIds('');
            } else {
                toast.error(res?.data?.statusDescription || 'Failed to trigger export report.');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Error triggering export.');
        } finally {
            setExportLoading(false);
        }
    };

    const handleViewDetail = async (record: any) => {
        try {
            const res = await getShippingRuleDetailService(record.shippingCriteriaId);
            if (res?.data?.data) {
                setSelectedRuleDetail(res.data.data);
            } else {
                setSelectedRuleDetail(record);
            }
            setDetailModalVisible(true);
        } catch (err: any) {
            setSelectedRuleDetail(record);
            setDetailModalVisible(true);
        }
    };

    const columns = [
        {
            title: 'Criteria ID / Category',
            dataIndex: 'shippingCriteriaId',
            key: 'shippingCriteriaId',
            render: (val: any) => {
                if (!val) return 'N/A';
                const catName = categoryMap[String(val)];
                return (
                    <div>
                        {catName ? (
                            <div>
                                <div className="font-semibold text-gray-800">{catName}</div>
                                <div className="text-xs text-gray-400">ID: {val}</div>
                            </div>
                        ) : (
                            <span className="font-medium text-gray-800">{val}</span>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            sorter: (a: any, b: any) => (a.priority || 0) - (b.priority || 0)
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean | string) => {
                const isActive = active === true || active === 'true' || active === '1';
                return (
                    <Tag color={isActive ? 'green' : 'red'}>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                );
            }
        },
        {
            title: 'Date Active',
            dataIndex: 'date_active',
            key: 'date_active',
            render: (val: string) => val ? new Date(val).toLocaleDateString() : 'N/A'
        },
        {
            title: 'Date Expiry',
            dataIndex: 'date_expiry',
            key: 'date_expiry',
            render: (val: string) => val ? new Date(val).toLocaleDateString() : 'N/A'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space size="middle">
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/shipping-portal/update/${record.shippingCriteriaId}`, { state: { record } })}
                    >
                        Edit
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container p-4">
                    <div className="flex items-center justify-between w-full mb-4">
                        <p className="text-xl font-bold text-[#43425D]">SHIPPING PORTAL</p>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/shipping-portal/create')}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Create Rule
                            </Button>
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => setBulkModalVisible(true)}
                            >
                                Bulk Upload
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={() => setExportModalVisible(true)}
                            >
                                Export Report
                            </Button>
                        </Space>
                    </div>

                    <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap items-center gap-4">
                        <div className="w-[240px]">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Search Criteria ID / Priority</label>
                            <Input
                                placeholder="Search..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                allowClear
                            />
                        </div>
                        <div className="w-[180px]">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                            <Select
                                value={statusFilter}
                                onChange={val => setStatusFilter(val)}
                                className="w-full"
                            >
                                <Select.Option value="ALL">All Status</Select.Option>
                                <Select.Option value="ACTIVE">Active Only</Select.Option>
                                <Select.Option value="INACTIVE">Inactive Only</Select.Option>
                            </Select>
                        </div>
                        <Button
                            onClick={() => { setSearchText(''); setStatusFilter('ALL'); }}
                            className="mt-5"
                        >
                            Reset Filters
                        </Button>
                    </div>

                    <div className="bg-white p-4 rounded shadow">
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            loading={loading}
                            rowKey={(rec, idx) => rec.shippingCriteriaId || idx}
                            pagination={{ pageSize: 15 }}
                        />
                    </div>
                </div>
            </div>

            {/* Bulk Upload Modal */}
            <ShippingBulkUploadModal
                visible={bulkModalVisible}
                onClose={() => setBulkModalVisible(false)}
                onSuccess={() => fetchRules()}
            />

            {/* Export Modal */}
            <Modal
                title="Export Shipping Rules Report"
                open={exportModalVisible}
                onCancel={() => setExportModalVisible(false)}
                onOk={handleExport}
                confirmLoading={exportLoading}
                okText="Export"
            >
                <div className="flex flex-col gap-4 mt-4">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Notification Email *</label>
                        <Input
                            type="email"
                            placeholder="Enter email address"
                            value={exportEmail}
                            onChange={e => setExportEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Category IDs (Optional, comma-separated)</label>
                        <Input
                            placeholder="e.g. CAT1001, CAT1002"
                            value={exportCategoryIds}
                            onChange={e => setExportCategoryIds(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>

            {/* View Detail Modal */}
            <Modal
                title={`Shipping Rule Details - ${selectedRuleDetail?.shippingCriteriaId || selectedRuleDetail?.shippingAttr?.shippingCriteriaId || ''}`}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="edit" type="primary" onClick={() => {
                        const ruleId = selectedRuleDetail?.shippingCriteriaId || selectedRuleDetail?.shippingAttr?.shippingCriteriaId;
                        setDetailModalVisible(false);
                        if (ruleId) {
                            navigate(`/shipping-portal/update/${ruleId}`, { state: { record: selectedRuleDetail } });
                        }
                    }}>
                        Edit Rule
                    </Button>,
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={750}
            >
                {selectedRuleDetail && (
                    <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto pr-1">
                        {/* Summary Header */}
                        <div className="bg-blue-50 border border-blue-100 p-3 rounded">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div>
                                    <span className="text-gray-500 font-semibold block">Criteria ID / Category:</span>
                                    <span className="font-bold text-gray-800">
                                        {(() => {
                                            const cid = String(selectedRuleDetail.shippingCriteriaId || selectedRuleDetail.shippingAttr?.shippingCriteriaId || '');
                                            return categoryMap[cid] ? `${categoryMap[cid]} (${cid})` : cid || 'N/A';
                                        })()}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500 font-semibold block">Priority:</span>
                                    <span className="font-bold text-gray-800">
                                        {selectedRuleDetail.priority ?? selectedRuleDetail.shippingAttr?.priority ?? 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500 font-semibold block">Status:</span>
                                    {(() => {
                                        const act = selectedRuleDetail.active ?? selectedRuleDetail.shippingAttr?.active;
                                        const isActive = act === true || act === 'true' || act === '1';
                                        return (
                                            <Tag color={isActive ? 'green' : 'red'}>
                                                {isActive ? 'ACTIVE' : 'INACTIVE'}
                                            </Tag>
                                        );
                                    })()}
                                </div>
                                <div>
                                    <span className="text-gray-500 font-semibold block">Active Period:</span>
                                    <span className="text-gray-700">
                                        {selectedRuleDetail.shippingAttr?.date_active
                                            ? new Date(selectedRuleDetail.shippingAttr.date_active).toLocaleDateString()
                                            : selectedRuleDetail.date_active ? new Date(selectedRuleDetail.date_active).toLocaleDateString() : 'N/A'}
                                        {' - '}
                                        {selectedRuleDetail.shippingAttr?.date_expiry
                                            ? new Date(selectedRuleDetail.shippingAttr.date_expiry).toLocaleDateString()
                                            : selectedRuleDetail.date_expiry ? new Date(selectedRuleDetail.date_expiry).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Criteria Blocks list if present */}
                        {Array.isArray(selectedRuleDetail.ruleCriteriaList) && selectedRuleDetail.ruleCriteriaList.length > 0 && (
                            <div className="flex flex-col gap-3">
                                <span className="font-semibold text-xs text-gray-600">Rule Criteria Blocks:</span>
                                {selectedRuleDetail.ruleCriteriaList.map((block: any, bIdx: number) => (
                                    <Card
                                        key={bIdx}
                                        size="small"
                                        title={`Criteria Block #${bIdx + 1} (${block.evaluationType || 'ANY_OF'}) - Priority ${block.priority || 1}`}
                                        className="bg-gray-50 border-gray-200"
                                    >
                                        <div className="flex flex-col gap-2">
                                            {Array.isArray(block.ruleList) && block.ruleList.map((rule: any, rIdx: number) => {
                                                const crit = rule.conditionsList?.[0]?.criteriaList?.[0];
                                                const catType = crit?.categoryType || 'CATEGORY';
                                                const attrVals = crit?.attributeList?.[0]?.attributeValues || [];
                                                
                                                const actionListCrit = Array.isArray(rule.actionList)
                                                    ? rule.actionList[0]
                                                    : rule.actionList?.criteriaList?.[0];
                                                const chargeAttr = actionListCrit?.attributeList?.find((a: any) =>
                                                    a.attributeType === 'SPECIAL_PRODUCT_SHIPPING_CHARGE' || a.attributeType === 'FIXED'
                                                ) || actionListCrit?.attributeList?.[0];
                                                const chargeVal = chargeAttr?.attributeValues?.[0] || chargeAttr?.attributeValue || '0';

                                                return (
                                                    <div key={rIdx} className="bg-white p-2 rounded border flex items-center justify-between gap-2 text-xs">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Tag color="geekblue">{catType}</Tag>
                                                            <div className="flex flex-wrap gap-1">
                                                                {attrVals.map((val: string, vIdx: number) => {
                                                                    const name = catType === 'CATEGORY' && categoryMap[val]
                                                                        ? `${categoryMap[val]} (${val})`
                                                                        : val;
                                                                    return (
                                                                        <Tag key={vIdx} color="default">
                                                                            {name || 'N/A'}
                                                                        </Tag>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                        <div className="text-right whitespace-nowrap">
                                                            <span className="text-gray-500">Charge: </span>
                                                            <span className="font-bold text-green-700">₹{chargeVal}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Raw JSON Collapse */}
                        <Collapse ghost size="small">
                            <Collapse.Panel header="Raw JSON Response" key="1">
                                <pre className="bg-gray-100 p-2 rounded text-[11px] overflow-auto max-h-[250px]">
                                    {JSON.stringify(selectedRuleDetail, null, 2)}
                                </pre>
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ShippingPortalListing;
